import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useReducer
} from 'react';
import { createPortal } from 'react-dom';
import styled, { ThemeProvider } from 'styled-components';
import { FeedbackModal } from './FeedbackModal.jsx';
import { FeedbackDashboard, saveFeedbackToLocalStorage } from './FeedbackDashboard.jsx';
import { CanvasOverlay } from './CanvasOverlay.jsx';
import { RecordingOverlay } from './RecordingOverlay.jsx';
import { SubmissionQueue } from './SubmissionQueue.jsx';
import ErrorToast, { showError, showSuccess, showInfo } from './ErrorToast.jsx';
import recorder from './recorder.js';
import { getElementInfo, captureElementScreenshot, getReactComponentInfo } from './utils.js';
import { getTheme, FeedbackGlobalStyle } from './theme.js';
import { IntegrationClient } from './integrations/index.js';
import { FeedbackDots } from './FeedbackDots.jsx';
import { MobileTrigger } from './MobileTrigger.jsx';

const FeedbackContext = createContext(null);

// Styled Components for FeedbackProvider
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme.colors.overlayBg};
  z-index: 999998;
  cursor: crosshair;
  pointer-events: none;
  transition: background 0.2s ease;
`;

const Highlight = styled.div`
  position: absolute;
  border: 2px solid ${props => props.theme.colors.highlightBorder};
  background: ${props => props.theme.colors.highlightBg};
  pointer-events: none;
  z-index: 999999;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 0 0 4px ${props => props.theme.colors.highlightShadow},
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-radius: 4px;
`;

const Tooltip = styled.div`
  position: fixed;
  background: ${props => props.theme.colors.tooltipBg};
  color: ${props => props.theme.colors.tooltipText};
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  font-weight: 500;
  pointer-events: none;
  z-index: 1000000;
  white-space: nowrap;
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.3),
    0 4px 6px -2px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
  transition: all 0.15s ease;
  max-width: 300px;
`;

const MobileBanner = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding: 12px 16px;
  background: ${props => props.theme.colors.btnPrimaryBg};
  color: white;
  font-size: 14px;
  font-weight: 600;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  text-align: center;
  z-index: 1000001;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  -webkit-tap-highlight-color: transparent;
`;

const MobileBannerClose = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:active {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const TooltipComponent = styled.span`
  color: #10b981;
  font-weight: 600;
`;

const TooltipTag = styled.span`
  color: ${props => props.theme.colors.tooltipText};
  opacity: 0.7;
`;

const initialState = {
  internalIsActive: false,
  hoveredElement: null,
  hoveredComponentInfo: null,
  selectedElement: null,
  highlightStyle: {},
  tooltipStyle: {},
  isModalOpen: false,
  screenshot: null,
  isCapturing: false,
  isDashboardOpen: false,
  isCanvasActive: false,
  isRecordingActive: false,
  isRecording: false,
  isInitializing: false,
  isPaused: false,
  videoBlob: null,
  eventLogs: [],
  isManualFeedbackOpen: false,
  // Integration state
  integrationStatus: {
    jira: { loading: false, error: null, result: null },
    sheets: { loading: false, error: null, result: null }
  },
  lastIntegrationResults: null,
  // Submission queue state
  submissionQueue: [],
  // Feedback dots state
  showFeedbackDots: false,
  clickPosition: null
};

function feedbackReducer(state, action) {
  switch (action.type) {
    case 'SET_STATE':
      return { ...state, ...action.payload };
    case 'START_HOVERING':
      return { ...state, hoveredElement: action.payload.element, hoveredComponentInfo: action.payload.componentInfo, highlightStyle: action.payload.highlightStyle, tooltipStyle: action.payload.tooltipStyle };
    case 'STOP_HOVERING':
      return { ...state, hoveredElement: null, hoveredComponentInfo: null };
    case 'START_CAPTURE':
      return { ...state, isCapturing: true, selectedElement: action.payload };
    case 'COMPLETE_CAPTURE':
      return { ...state, isCapturing: false, screenshot: action.payload, isModalOpen: true, hoveredElement: null, hoveredComponentInfo: null };
    case 'CANCEL_CAPTURE':
      return { ...state, isCapturing: false, hoveredElement: null, hoveredComponentInfo: null };
    case 'OPEN_DASHBOARD':
      return { ...state, isDashboardOpen: true };
    case 'CLOSE_DASHBOARD':
      return { ...state, isDashboardOpen: false };
    case 'OPEN_MANUAL_FEEDBACK':
      return { ...state, isManualFeedbackOpen: true, isModalOpen: true, screenshot: null, videoBlob: null };
    case 'CLOSE_MANUAL_FEEDBACK':
      return { ...state, isManualFeedbackOpen: false, isModalOpen: false };
    case 'START_RECORDING_INIT':
      return { ...state, isInitializing: true };
    case 'START_RECORDING_SUCCESS':
      return { ...state, isInitializing: false, isRecordingActive: true, isRecording: true, isPaused: false };
    case 'START_RECORDING_FAILURE':
      return { ...state, isInitializing: false, isRecording: false, isRecordingActive: false };
    case 'PAUSE_RECORDING':
      return { ...state, isPaused: true };
    case 'RESUME_RECORDING':
      return { ...state, isPaused: false };
    case 'CANCEL_RECORDING':
      return { ...state, isRecordingActive: false, isRecording: false, isInitializing: false, isPaused: false, videoBlob: null, eventLogs: [] };
    case 'STOP_RECORDING':
      return { ...state, isRecordingActive: false, isRecording: false, isInitializing: false, isPaused: false, videoBlob: action.payload.blob, eventLogs: action.payload.events, isModalOpen: action.payload.blob && action.payload.blob.size > 0 };
    case 'RESET_MODAL':
      return { ...state, isModalOpen: false, isManualFeedbackOpen: false, selectedElement: null, screenshot: null, hoveredElement: null, hoveredComponentInfo: null, isCanvasActive: false, videoBlob: null, eventLogs: [], lastIntegrationResults: null, clickPosition: null };
    // Integration actions
    case 'INTEGRATION_START':
      return {
        ...state,
        integrationStatus: {
          jira: action.payload.jira ? { loading: true, error: null, result: null } : state.integrationStatus.jira,
          sheets: action.payload.sheets ? { loading: true, error: null, result: null } : state.integrationStatus.sheets
        }
      };
    case 'INTEGRATION_SUCCESS':
      return {
        ...state,
        integrationStatus: {
          jira: action.payload.jira ? { loading: false, error: null, result: action.payload.jira } : state.integrationStatus.jira,
          sheets: action.payload.sheets ? { loading: false, error: null, result: action.payload.sheets } : state.integrationStatus.sheets
        },
        lastIntegrationResults: action.payload
      };
    case 'INTEGRATION_ERROR':
      return {
        ...state,
        integrationStatus: {
          jira: action.payload.jira?.error ? { loading: false, error: action.payload.jira.error, result: null } : state.integrationStatus.jira,
          sheets: action.payload.sheets?.error ? { loading: false, error: action.payload.sheets.error, result: null } : state.integrationStatus.sheets
        },
        lastIntegrationResults: action.payload
      };
    case 'INTEGRATION_RESET':
      return {
        ...state,
        integrationStatus: {
          jira: { loading: false, error: null, result: null },
          sheets: { loading: false, error: null, result: null }
        },
        lastIntegrationResults: null
      };
    // Submission queue actions
    case 'ADD_SUBMISSION':
      return {
        ...state,
        submissionQueue: [...state.submissionQueue, action.payload]
      };
    case 'UPDATE_SUBMISSION':
      return {
        ...state,
        submissionQueue: state.submissionQueue.map(sub =>
          sub.id === action.payload.id ? { ...sub, ...action.payload } : sub
        )
      };
    case 'REMOVE_SUBMISSION':
      return {
        ...state,
        submissionQueue: state.submissionQueue.filter(sub => sub.id !== action.payload)
      };
    // Feedback dots actions
    case 'TOGGLE_FEEDBACK_DOTS':
      return { ...state, showFeedbackDots: !state.showFeedbackDots };
    case 'SET_FEEDBACK_DOTS':
      return { ...state, showFeedbackDots: action.payload };
    case 'SET_CLICK_POSITION':
      return { ...state, clickPosition: action.payload };
    default:
      return state;
  }
}

export const FeedbackProvider = ({
  children,
  onSubmit,
  isActive: controlledIsActive,
  onActiveChange,
  dashboard = false,
  dashboardData,
  isDeveloper = false,
  isUser = true,
  userName,
  userEmail,
  userAvatar,
  onStatusChange,
  mode = 'light',
  defaultOpen = false,
  // Integration configuration
  integrations = null,
  onIntegrationSuccess,
  onIntegrationError,
  // Feedback dots
  showFeedbackDots: showFeedbackDotsProp,
  feedbackDotsData,
  // Mobile
  enableMobileFeedback = true
}) => {
  const [state, dispatch] = useReducer(feedbackReducer, initialState);
  const {
    internalIsActive,
    hoveredElement,
    hoveredComponentInfo,
    selectedElement,
    highlightStyle,
    tooltipStyle,
    isModalOpen,
    screenshot,
    isCapturing,
    isDashboardOpen,
    isCanvasActive,
    isRecordingActive,
    isRecording,
    isInitializing,
    isPaused,
    videoBlob,
    eventLogs,
    isManualFeedbackOpen,
    integrationStatus,
    lastIntegrationResults,
    submissionQueue,
    showFeedbackDots,
    clickPosition
  } = state;

  // Initialize integration client
  const integrationClientRef = useRef(null);

  useEffect(() => {
    if (integrations && (integrations.jira?.enabled || integrations.sheets?.enabled)) {
      integrationClientRef.current = new IntegrationClient({
        jira: integrations.jira,
        sheets: integrations.sheets,
        onSuccess: (type, result) => {
          if (onIntegrationSuccess) {
            onIntegrationSuccess(type, result);
          }
        },
        onError: (type, error) => {
          if (onIntegrationError) {
            onIntegrationError(type, error);
          }
        }
      });
    } else {
      integrationClientRef.current = null;
    }
  }, [integrations?.jira?.enabled, integrations?.sheets?.enabled, onIntegrationSuccess, onIntegrationError]);

  // Determine if component is controlled
  const isControlled = controlledIsActive !== undefined;
  const isActive = isControlled ? controlledIsActive : internalIsActive;

  useEffect(() => {
    if (defaultOpen) {
      dispatch({ type: 'OPEN_MANUAL_FEEDBACK' });
    }
  }, [defaultOpen]);

  useEffect(() => {
    if (showFeedbackDotsProp !== undefined) {
      dispatch({ type: 'SET_FEEDBACK_DOTS', payload: showFeedbackDotsProp });
    }
  }, [showFeedbackDotsProp]);

  // Handle state changes
  const setIsActive = useCallback((newValue) => {
    if (isControlled) {
      if (onActiveChange) {
        onActiveChange(typeof newValue === 'function' ? newValue(isActive) : newValue);
      }
    } else {
      dispatch({ type: 'SET_STATE', payload: { internalIsActive: newValue } });
    }
  }, [isControlled, onActiveChange, isActive]);

  const overlayRef = useRef(null);
  const highlightRef = useRef(null);
  const startRecordingRef = useRef(null);

  const theme = getTheme(mode);

  useEffect(() => {
    if (!onSubmit || typeof onSubmit !== 'function') {
      // onSubmit function is required
    }
  }, [onSubmit]);

  const isValidElement = useCallback((element) => {
    if (!element || element === document.body || element === document.documentElement) {
      return false;
    }

    if (element.closest('.feedback-overlay, .feedback-modal, .feedback-backdrop, .feedback-tooltip, .feedback-highlight, .feedback-dots-container')) {
      return false;
    }

    const rect = element.getBoundingClientRect();
    if (rect.width < 10 || rect.height < 10) {
      return false;
    }

    return true;
  }, []);

  // Throttle helper
  const throttleRef = useRef(null);
  const lastElementRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!isActive) return;

    // Throttle to 60fps max (16ms)
    if (throttleRef.current) return;
    throttleRef.current = requestAnimationFrame(() => {
      throttleRef.current = null;

      const element = document.elementFromPoint(e.clientX, e.clientY);
      if (!isValidElement(element) || element === lastElementRef.current) return;

      lastElementRef.current = element;
      const componentInfo = getReactComponentInfo(element);
      const rect = element.getBoundingClientRect();
      const scrollX = window.pageXOffset;
      const scrollY = window.pageYOffset;

      dispatch({
        type: 'START_HOVERING',
        payload: {
          element,
          componentInfo,
          highlightStyle: {
            left: rect.left + scrollX,
            top: rect.top + scrollY,
            width: rect.width,
            height: rect.height,
          },
          tooltipStyle: {
            left: Math.min(e.clientX + 10, window.innerWidth - 300),
            top: Math.max(e.clientY - 40, 10),
          }
        }
      });
    });
  }, [isActive, isValidElement]);

  const handleElementClick = useCallback(async (e) => {
    if (!isActive || !hoveredElement) return;

    e.preventDefault();
    e.stopPropagation();

    // Capture click position relative to the element (0-1 range)
    const rect = hoveredElement.getBoundingClientRect();
    const relativeX = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const relativeY = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    dispatch({ type: 'SET_CLICK_POSITION', payload: { relativeX, relativeY } });

    dispatch({ type: 'START_CAPTURE', payload: hoveredElement });

    try {
      const screenshotData = await captureElementScreenshot(hoveredElement);
      dispatch({ type: 'COMPLETE_CAPTURE', payload: screenshotData });
    } catch (error) {
      showError('Failed to capture screenshot. You can still submit feedback.', 'Capture Error');
      dispatch({ type: 'COMPLETE_CAPTURE', payload: null });
    }
  }, [isActive, hoveredElement]);

  const handleKeyDown = useCallback((e) => {
    // Alt+Q - Activate feedback mode (element selection)
    if (e.altKey && !e.shiftKey && (e.key.toLowerCase() === 'q' || e.keyCode === 81 || e.code === 'KeyQ')) {
      e.preventDefault();
      if (!isActive && !isRecording) {
        setIsActive(true);
        dispatch({ type: 'SET_STATE', payload: { isCanvasActive: false } });
      }
      return;
    }

    // Alt+A - Open form directly (Manual Feedback)
    if (e.altKey && !e.shiftKey && (e.key.toLowerCase() === 'a' || e.keyCode === 65 || e.code === 'KeyA')) {
      e.preventDefault();
      if (!isModalOpen && !isActive && !isRecording) {
        dispatch({ type: 'OPEN_MANUAL_FEEDBACK' });
      }
      return;
    }

    // Alt+W - Start video recording
    if (e.altKey && !e.shiftKey && (e.key.toLowerCase() === 'w' || e.keyCode === 87 || e.code === 'KeyW')) {
      e.preventDefault();
      if (!isRecording && !isActive && startRecordingRef.current) {
        startRecordingRef.current();
      }
      return;
    }

    // Alt+D - Toggle feedback dots
    if (e.altKey && !e.shiftKey && (e.key.toLowerCase() === 'd' || e.keyCode === 68 || e.code === 'KeyD')) {
      e.preventDefault();
      dispatch({ type: 'TOGGLE_FEEDBACK_DOTS' });
      return;
    }

    // Alt+Shift+Q - Open dashboard
    if (dashboard && e.altKey && e.shiftKey && (e.key.toLowerCase() === 'q' || e.keyCode === 81 || e.code === 'KeyQ')) {
      e.preventDefault();
      dispatch({ type: 'OPEN_DASHBOARD' });
      return;
    }

    // Escape - Cancel/close
    if (e.key === 'Escape') {
      e.preventDefault();
      if (isActive) {
        setIsActive(false);
        dispatch({ type: 'RESET_MODAL' });
      }
      if (isDashboardOpen) {
        dispatch({ type: 'CLOSE_DASHBOARD' });
      }
    }
  }, [isActive, isDashboardOpen, dashboard, setIsActive, isRecording]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (isActive) {
      document.body.classList.add('feedback-mode-active');
    } else {
      document.body.classList.remove('feedback-mode-active');
    }

    return () => {
      document.body.classList.remove('feedback-mode-active');
    };
  }, [isActive]);

  useEffect(() => {
    if (!isActive) {
      dispatch({ type: 'STOP_HOVERING' });
      dispatch({ type: 'SET_STATE', payload: { selectedElement: null } });
      return;
    }

    if (!isCanvasActive && !isModalOpen) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('click', handleElementClick, true);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('click', handleElementClick, true);
      };
    }
  }, [isActive, isCanvasActive, isModalOpen, handleMouseMove, handleElementClick]);

  // --- Mobile: detect touch device ---
  const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  // --- Mobile: touch-based element selection (replaces hover on touch devices) ---
  const handleTouchSelect = useCallback(async (e) => {
    if (!isActive || isCanvasActive || isModalOpen) return;

    const touch = e.touches[0];
    if (!touch) return;

    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!isValidElement(element)) return;

    e.preventDefault();

    // Highlight the element
    const componentInfo = getReactComponentInfo(element);
    const rect = element.getBoundingClientRect();
    const scrollX = window.pageXOffset;
    const scrollY = window.pageYOffset;

    dispatch({
      type: 'START_HOVERING',
      payload: {
        element,
        componentInfo,
        highlightStyle: {
          left: rect.left + scrollX,
          top: rect.top + scrollY,
          width: rect.width,
          height: rect.height,
        },
        tooltipStyle: { left: -9999, top: -9999 } // hide tooltip on mobile
      }
    });

    // Capture click position
    const relativeX = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
    const relativeY = Math.max(0, Math.min(1, (touch.clientY - rect.top) / rect.height));
    dispatch({ type: 'SET_CLICK_POSITION', payload: { relativeX, relativeY } });

    // Brief highlight then capture
    dispatch({ type: 'START_CAPTURE', payload: element });

    try {
      const screenshotData = await captureElementScreenshot(element);
      dispatch({ type: 'COMPLETE_CAPTURE', payload: screenshotData });
    } catch (error) {
      showError('Failed to capture screenshot.', 'Capture Error');
      dispatch({ type: 'COMPLETE_CAPTURE', payload: null });
    }
  }, [isActive, isCanvasActive, isModalOpen, isValidElement]);

  useEffect(() => {
    if (!isTouchDevice || !isActive || isCanvasActive || isModalOpen) return;

    document.addEventListener('touchstart', handleTouchSelect, { passive: false });
    return () => document.removeEventListener('touchstart', handleTouchSelect);
  }, [isTouchDevice, isActive, isCanvasActive, isModalOpen, handleTouchSelect]);

  // --- Mobile: activate feedback mode ---
  const openScreenshotMode = useCallback(() => {
    if (!isActive && !isRecording) {
      setIsActive(true);
      dispatch({ type: 'SET_STATE', payload: { isCanvasActive: true } });
    }
  }, [isActive, isRecording, setIsActive]);

  // Generate unique ID for submissions
  const generateSubmissionId = useCallback(() => {
    return `sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Async submission handler that adds to queue and processes in background
  const handleAsyncSubmit = useCallback((feedbackData) => {
    const submissionId = generateSubmissionId();

    // Add to queue immediately with 'submitting' status
    dispatch({
      type: 'ADD_SUBMISSION',
      payload: { id: submissionId, status: 'submitting' }
    });

    // Reset modal state immediately (don't wait for submission)
    setIsActive(false);
    dispatch({ type: 'RESET_MODAL' });

    // Process submission in background with timeout (60 seconds for large files)
    const hasLargeMedia = feedbackData.videoBlob && feedbackData.videoBlob.size > 5 * 1024 * 1024; // 5MB+
    const timeoutMs = hasLargeMedia ? 60000 : 30000; // 1 min for large, 30s otherwise

    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Submission timed out')), timeoutMs);
    });

    const submitPromise = (async () => {
      let processedData = { ...feedbackData };

      // Keep videoBlob as-is for FormData upload
      if (feedbackData.videoBlob && feedbackData.videoBlob instanceof Blob) {
        processedData.videoBlob = feedbackData.videoBlob;
        processedData.videoSize = feedbackData.videoBlob.size;
        processedData.videoType = feedbackData.videoBlob.type;
      }

      // Extract selectedIntegrations from feedback data
      const selectedIntegrations = feedbackData.selectedIntegrations || {
        local: true,
        jira: integrations?.jira?.enabled || false,
        sheets: integrations?.sheets?.enabled || false
      };

      // Save to local storage if dashboard is enabled AND local is selected
      if (dashboard && selectedIntegrations.local) {
        await saveFeedbackToLocalStorage(processedData);
      }

      if (onSubmit && typeof onSubmit === 'function') {
        await onSubmit(processedData);
      }

      // Send to integrations if configured AND selected
      const shouldSendToJira = integrationClientRef.current && integrations?.jira?.enabled && selectedIntegrations.jira;
      const shouldSendToSheets = integrationClientRef.current && integrations?.sheets?.enabled && selectedIntegrations.sheets;

      if (shouldSendToJira || shouldSendToSheets) {
        try {
          const integrationResults = await integrationClientRef.current.sendFeedback(processedData, {
            jira: shouldSendToJira,
            sheets: shouldSendToSheets
          });

          if (integrationResults.jira?.success && integrationResults.jira?.issueKey) {
            processedData.jiraKey = integrationResults.jira.issueKey;
            processedData.jiraUrl = integrationResults.jira.issueUrl;

            if (dashboard && selectedIntegrations.local) {
              await saveFeedbackToLocalStorage({
                ...processedData,
                jiraKey: integrationResults.jira.issueKey,
                jiraUrl: integrationResults.jira.issueUrl
              });
            }
          }
        } catch (integrationError) {
          // Log but don't fail the submission
          console.warn('Integration error:', integrationError.message);
        }
      }

      return processedData;
    })();

    // Race between submission and timeout
    Promise.race([submitPromise, timeoutPromise])
      .then(() => {
        // Update to success
        dispatch({
          type: 'UPDATE_SUBMISSION',
          payload: { id: submissionId, status: 'success' }
        });

        // Auto-remove success after 3 seconds
        setTimeout(() => {
          dispatch({ type: 'REMOVE_SUBMISSION', payload: submissionId });
        }, 3000);
      })
      .catch((error) => {
        // Update to error
        dispatch({
          type: 'UPDATE_SUBMISSION',
          payload: { id: submissionId, status: 'error', error: error.message }
        });

        // Auto-remove error after 5 seconds
        setTimeout(() => {
          dispatch({ type: 'REMOVE_SUBMISSION', payload: submissionId });
        }, 5000);
      });
  }, [onSubmit, dashboard, setIsActive, integrations, generateSubmissionId]);

  // Handler to dismiss a submission from the queue
  const handleDismissSubmission = useCallback((submissionId) => {
    dispatch({ type: 'REMOVE_SUBMISSION', payload: submissionId });
  }, []);

  // Legacy synchronous submit (kept for backward compatibility)
  const handleFeedbackSubmit = useCallback(async (feedbackData) => {
    // Use async handler instead
    handleAsyncSubmit(feedbackData);
  }, [handleAsyncSubmit]);

  const handleCloseModal = useCallback(() => {
    setIsActive(false);
    dispatch({ type: 'RESET_MODAL' });
  }, [setIsActive]);

  const handleCanvasComplete = useCallback(async (canvasScreenshot, feedbackText) => {
    setIsActive(false);
    dispatch({ type: 'SET_STATE', payload: { isCanvasActive: false } });

    const feedbackData = {
      feedback: feedbackText,
      elementInfo: null,
      screenshot: canvasScreenshot,
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: { width: window.innerWidth, height: window.innerHeight },
      timestamp: new Date().toISOString(),
      userName: userName || 'Anonymous',
      userEmail: userEmail || null
    };

    try {
      if (dashboard) {
        const result = await saveFeedbackToLocalStorage(feedbackData);
        if (!result.success) {
          throw new Error(result.error);
        }
      }

      if (onSubmit && typeof onSubmit === 'function') {
        await onSubmit(feedbackData);
      }

      showSuccess('Feedback submitted successfully!', 'Success');
      dispatch({ type: 'STOP_HOVERING' });
      dispatch({ type: 'SET_STATE', payload: { selectedElement: null, screenshot: null } });
    } catch (error) {
      showError(error.message || 'Failed to save feedback. Please try again.', 'Submission Error');
    }
  }, [dashboard, onSubmit, userName, userEmail, setIsActive]);

  const handleCanvasCancel = useCallback(() => {
    setIsActive(false);
    dispatch({ type: 'SET_STATE', payload: { isCanvasActive: false } });
  }, [setIsActive]);

  const handleStartRecording = useCallback(async () => {
    try {
      dispatch({ type: 'START_RECORDING_INIT' });
      await recorder.start();
      dispatch({ type: 'START_RECORDING_SUCCESS' });
    } catch (error) {
      dispatch({ type: 'START_RECORDING_FAILURE' });
      showError('Could not start recording. Please ensure you have granted screen and microphone permissions.', 'Recording Error');
    }
  }, []);

  startRecordingRef.current = handleStartRecording;

  const handlePauseRecording = useCallback(() => {
    recorder.pause();
    dispatch({ type: 'PAUSE_RECORDING' });
  }, []);

  const handleResumeRecording = useCallback(() => {
    recorder.resume();
    dispatch({ type: 'RESUME_RECORDING' });
  }, []);
  
  const handleCancelRecording = useCallback(async () => {
    await recorder.stop();
    dispatch({ type: 'CANCEL_RECORDING' });
  }, []);

  const handleStopRecording = useCallback(async () => {
    try {
      const { videoBlob: blob, events } = await recorder.stop();
      dispatch({ type: 'STOP_RECORDING', payload: { blob, events } });
      if (!blob || blob.size === 0) {
        showError('Recording failed: No video data was captured. Please try again.', 'Recording Error');
      }
    } catch (error) {
      showError('Failed to stop recording properly. Please try again.', 'Recording Error');
      dispatch({ type: 'CANCEL_RECORDING' });
    }
  }, []);

  const setIsDashboardOpen = useCallback((value) => {
    if (value) {
      dispatch({ type: 'OPEN_DASHBOARD' });
    } else {
      dispatch({ type: 'CLOSE_DASHBOARD' });
    }
  }, []);

  const getTooltipContent = () => {
    if (!hoveredElement) return null;
    const tagName = hoveredElement.tagName.toLowerCase();
    const id = hoveredElement.id ? `#${hoveredElement.id}` : '';
    const componentName = hoveredComponentInfo?.componentName;
    return { tagName, id, componentName };
  };

  const tooltipContent = getTooltipContent();


  return (
    <FeedbackContext.Provider value={{
      isActive,
      setIsActive,
      setIsDashboardOpen,
      startRecording: handleStartRecording,
      integrationStatus,
      integrations,
      integrationClient: integrationClientRef.current,
      showFeedbackDots,
      toggleFeedbackDots: () => dispatch({ type: 'TOGGLE_FEEDBACK_DOTS' }),
      openScreenshotMode
    }}>
      <ThemeProvider theme={theme}>
        <FeedbackGlobalStyle />
        {children}

        {isActive && !isCanvasActive && !isModalOpen && createPortal(
          <>
            <Overlay ref={overlayRef} />

            {isTouchDevice && (
              <MobileBanner>
                Tap any element to report feedback
                <MobileBannerClose onClick={() => {
                  setIsActive(false);
                  dispatch({ type: 'RESET_MODAL' });
                }}>
                  Cancel
                </MobileBannerClose>
              </MobileBanner>
            )}

            {hoveredElement && (
              <>
                <Highlight
                  ref={highlightRef}
                  style={highlightStyle}
                />
                <Tooltip style={tooltipStyle}>
                  {tooltipContent?.componentName && (
                    <>
                      <TooltipComponent>&lt;{tooltipContent.componentName}&gt;</TooltipComponent>
                      {' '}
                    </>
                  )}
                  <TooltipTag>
                    {tooltipContent?.tagName}
                    {tooltipContent?.id}
                  </TooltipTag>
                  {isCapturing && ' (Capturing...)'}
                </Tooltip>
              </>
            )}
          </>,
          document.body
        )}

        <FeedbackModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          elementInfo={selectedElement ? getElementInfo(selectedElement) : null}
          screenshot={screenshot}
          videoBlob={videoBlob}
          eventLogs={eventLogs}
          onSubmit={handleFeedbackSubmit}
          onAsyncSubmit={handleAsyncSubmit}
          userName={userName}
          userEmail={userEmail}
          userAvatar={userAvatar}
          mode={mode}
          isManual={isManualFeedbackOpen}
          integrations={integrations}
          clickPosition={clickPosition}
        />

        <CanvasOverlay
          isActive={isCanvasActive}
          onComplete={handleCanvasComplete}
          onCancel={handleCanvasCancel}
          mode={mode}
        />

        <RecordingOverlay
          isRecording={isRecording}
          isInitializing={isInitializing}
          isPaused={isPaused}
          onStop={handleStopRecording}
          onPause={handlePauseRecording}
          onResume={handleResumeRecording}
          onCancel={handleCancelRecording}
          mode={mode}
        />

        {dashboard && (
          <FeedbackDashboard
            isOpen={isDashboardOpen}
            onClose={() => setIsDashboardOpen(false)}
            data={dashboardData}
            isDeveloper={isDeveloper}
            isUser={isUser}
            onStatusChange={onStatusChange}
            userName={userName}
            userEmail={userEmail}
            mode={mode}
            integrations={integrations}
            integrationClient={integrationClientRef.current}
          />
        )}

        <ErrorToast />

        <SubmissionQueue
          submissions={submissionQueue}
          onDismiss={handleDismissSubmission}
          mode={mode}
        />

        <FeedbackDots
          mode={mode}
          isDeveloper={isDeveloper}
          data={feedbackDotsData}
          visible={showFeedbackDots && !isActive}
        />

        {enableMobileFeedback && isTouchDevice && (
          <MobileTrigger
            mode={mode}
            isActive={isActive}
            onActivate={() => {
              if (!isActive && !isRecording) {
                setIsActive(true);
                dispatch({ type: 'SET_STATE', payload: { isCanvasActive: false } });
              }
            }}
            onCancel={() => {
              setIsActive(false);
              dispatch({ type: 'RESET_MODAL' });
            }}
          />
        )}
      </ThemeProvider>
    </FeedbackContext.Provider>
  );
};

export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used within FeedbackProvider');
  }
  return context;
};
